import { memo, useEffect, useMemo, useRef, useState } from "react";
import { PlugZap, Unplug } from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { rtctApi } from "services/rtctApi";
import { duration, formatBytes } from "utils/fieldFormat";

// Live mode: the node samples the radar every few seconds while it is on
// external power. The backend and the firmware both enforce the limits; this
// panel explains them before a command is sent.
const RADAR = [5, 10, 30, 60];
const WINDOWS = [
  [30, "30 min"],
  [60, "1 h"],
  [120, "2 h"],
  [240, "4 h"],
];
const CAMERAS = [
  [0, "Off"],
  [30, "30 s"],
  [120, "2 min"],
  [600, "10 min"],
];
const SIZES = ["VGA", "SVGA", "XGA", "UXGA"];
const FAST = ["starting", "stopping", "running"];
const WINDOW_OPEN = ["running", "starting", "armed", "silent"];
const HOLD_MS = 4 * 3600 * 1000;
const CAMERA_PASS_S = 15; // firmware: one camera pass is about 15 s of rail work
const PHASES = {
  running: ["Live", "is-running"],
  starting: ["Waiting for the node", "is-waiting"],
  stopping: ["Stopping", "is-waiting"],
  silent: ["No live samples", "is-waiting"],
  armed: ["Armed · not sampling", "is-waiting"],
  off: ["Off", "is-off"],
};
const REQUEST_STATUS = {
  queued: "being sent",
  sent: "waiting for the node to pick it up",
  acknowledged: "received by the node",
  confirmed: "confirmed by the node",
  publish_failed: "could not reach the broker",
  superseded: "replaced by a newer command",
  rejected: "rejected by the node",
};

const sgt = (value, seconds = false) =>
  new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    hour: "2-digit",
    minute: "2-digit",
    ...(seconds ? { second: "2-digit" } : {}),
    hour12: false,
  }).format(new Date(value));

// Kept as an export here for the panels that import it from Live mode.
export { duration };
const signed = (v, digits = 1) =>
  `${v > 0 ? "+" : v < 0 ? "−" : "±"}${Math.abs(v).toFixed(digits)}`;
const megabytes = (bytes) =>
  bytes < 100000
    ? `${Math.max(1, Math.round(bytes / 1000))} kB`
    : `${(bytes / 1e6).toLocaleString(undefined, { maximumFractionDigits: bytes < 1e7 ? 1 : 0 })} MB`;

export function mergePoints(old, incoming, now = Date.now()) {
  const seen = new Set(old.map((p) => p.t));
  return old
    .concat(incoming.filter((p) => !seen.has(p.t)))
    .filter((p) => Date.parse(p.t) >= now - HOLD_MS)
    .sort((a, b) => Date.parse(a.t) - Date.parse(b.t));
}

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Session figures from live samples only: change since the first, the trend
// of the last five minutes, sensor noise, and the cadence the node achieved.
// Noise is the median absolute deviation of successive differences, scaled to
// a standard deviation: a moving level, pump steps and single spikes do not
// count as noise.
export function liveStats(points) {
  const at = (p) => Date.parse(p.t);
  const live = points.filter((p) => p.live);
  const valid = live.filter((p) => typeof p.d === "number");
  if (!valid.length) return null;
  const first = valid[0],
    latest = valid[valid.length - 1];
  const recent = valid.filter((p) => at(p) >= at(latest) - 5 * 60000);
  let slope = null;
  if (recent.length >= 3 && at(latest) - at(recent[0]) >= 60000) {
    const xs = recent.map((p) => (at(p) - at(recent[0])) / 60000);
    const mx = xs.reduce((a, b) => a + b, 0) / xs.length;
    const my = recent.reduce((a, p) => a + p.d, 0) / recent.length;
    const sxx = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
    slope = sxx
      ? xs.reduce((a, x, i) => a + (x - mx) * (recent[i].d - my), 0) / sxx
      : null;
  }
  const tail = valid.slice(-30);
  const diffs = tail.slice(1).map((p, i) => p.d - tail[i].d);
  const centre = diffs.length >= 5 ? median(diffs) : 0;
  const noise =
    diffs.length >= 5
      ? (1.4826 * median(diffs.map((d) => Math.abs(d - centre)))) / Math.SQRT2
      : null;
  const gaps = live
    .slice(1)
    .map((p, i) => (at(p) - at(live[i])) / 1000)
    .filter((g) => g > 0 && g < 900)
    .sort((a, b) => a - b);
  return {
    first,
    latest,
    change: latest.d - first.d,
    slope,
    noise,
    cadence: gaps.length ? gaps[Math.floor(gaps.length / 2)] : null,
    count: live.length,
    validShare: valid.length / live.length,
  };
}

export function liveEstimate(draft, estimate) {
  if (!draft || !estimate) return null;
  const radar = (3600 / draft.live_s) * estimate.sample_bytes;
  const pixels = estimate.frame_pixels?.[draft.live_cam_fs] || 0;
  const photos = draft.live_cam_s
    ? estimate.image_bytes_per_pixel
      ? (3600 / draft.live_cam_s) * 2 * estimate.image_bytes_per_pixel * pixels
      : null
    : 0;
  const hourly = radar + (photos || 0);
  return { radar, photos, hourly, window: (hourly * draft.live_min) / 60 };
}

const LiveChart = memo(function LiveChart({ data, baseline }) {
  return (
    <div className="field-live-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="t"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(v) => sgt(v)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            minTickGap={60}
          />
          <YAxis
            domain={["dataMin - 2", "dataMax + 2"]}
            tickFormatter={(v) => Math.round(v)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" mm"
            width={66}
          />
          <Tooltip
            labelFormatter={(v) => `${sgt(v, true)} SGT`}
            formatter={(value, name) => [`${Number(value).toFixed(1)} mm`, name]}
          />
          {baseline !== null && (
            <ReferenceLine y={baseline} stroke="#9fb6bf" strokeDasharray="4 4" />
          )}
          <Line
            dataKey="scheduled"
            name="Scheduled sample"
            stroke="none"
            dot={{ r: 2.5, fill: "#9fb1b9", stroke: "none" }}
            isAnimationActive={false}
          />
          <Line
            dataKey="live"
            name="Live sample"
            stroke="#087f70"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

function Choice({ label, options, value, onChange, disabled }) {
  return (
    <div className="field-live-control">
      <span>{label}</span>
      <div className="field-segment" role="group" aria-label={label}>
        {options.map(([v, text]) => (
          <button
            key={v}
            type="button"
            className={value === v ? "active" : ""}
            aria-pressed={value === v}
            disabled={disabled}
            onClick={() => onChange(v)}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

const draftFrom = (live) => {
  const open = WINDOW_OPEN.includes(live.phase);
  const last = live.request?.action === "start" ? live.request : null;
  // Live samples do not carry the frame size and the node's full settings report
  // waits for the session to end, so the latest start command is the authority.
  const fs = last?.values.live_cam_fs || live.camera_framesize;
  return {
    live_s: (open && live.interval_s) || last?.values.live_s || 10,
    live_min: last?.values.live_min || 240,
    live_cam_s: (open ? live.camera_s : last?.values.live_cam_s) || 0,
    live_cam_fs: SIZES.includes(fs) ? fs : "VGA",
    keep: last ? last.keep : true,
  };
};

export default function LivePanel({ mac, storage, onState }) {
  const [live, setLive] = useState(null);
  const [points, setPoints] = useState([]);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const lastSample = useRef(null);

  useEffect(() => {
    if (!mac) return;
    const abort = new AbortController();
    rtctApi
      .live(mac, abort.signal, lastSample.current)
      .then(({ samples = [], ...state }) => {
        if (abort.signal.aborted) return;
        setLive(state);
        setError("");
        setNow(Date.now());
        if (samples.length) {
          lastSample.current = samples[samples.length - 1].t;
          setPoints((old) => mergePoints(old, samples));
        }
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [mac, tick]);

  const phase = live?.phase || "off";
  // Poll at the node's own pace while something is happening; the page's
  // usual 30 s otherwise. Hidden tabs ask nothing.
  const delay = FAST.includes(phase)
    ? Math.min(15, Math.max(5, live?.interval_s || 10)) * 1000
    : 30000;
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setTick((n) => n + 1);
    }, delay);
    return () => clearInterval(timer);
  }, [delay]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (live && !draft) setDraft(draftFrom(live));
  }, [live, draft]);
  // The page passes the live state on (the radar streams only while running).
  useEffect(() => {
    if (live) onState?.(live);
  }, [live, onState]);

  const stats = useMemo(() => liveStats(points), [points]);
  const chartData = useMemo(
    () =>
      points.map((p) => ({
        t: Date.parse(p.t),
        live: p.live ? p.d : null,
        scheduled: p.live ? null : p.d,
      })),
    [points],
  );
  const estimate = useMemo(
    () => liveEstimate(draft, live?.estimate),
    [draft, live],
  );

  const send = async (body) => {
    setBusy(true);
    setActionError("");
    try {
      const data = await rtctApi.setLive(mac, body);
      setLive((old) => ({ ...old, ...data }));
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
      setTick((n) => n + 1);
    }
  };
  const start = () =>
    send({
      action: "start",
      live_s: draft.live_s,
      live_min: draft.live_min,
      live_cam_s: draft.live_cam_s,
      ...(draft.live_cam_s ? { live_cam_fs: draft.live_cam_fs } : {}),
      keep: draft.keep,
    });
  const change = (key) => (value) =>
    setDraft((old) => ({ ...old, [key]: value }));

  const power = live?.power;
  const open = WINDOW_OPEN.includes(phase);
  const until = live?.until ? Date.parse(live.until) : null;
  const remaining = until ? Math.max(0, (until - now) / 1000) : 0;
  const span = live?.request?.action === "start" ? live.request.values.live_min * 60 : null;
  const progress = until && span ? Math.min(1, Math.max(0, 1 - remaining / span)) : null;
  const floor = live?.battery_floor_v ?? 3.25;
  const nearFloor =
    power?.ok &&
    typeof power.battery_v === "number" &&
    power.battery_v >= 1.5 &&
    power.battery_v < floor + 0.03;
  const waiting = live?.settings_waiting_since;
  const cellular = ["lte", "cellular", "nbiot"].includes(
    String(live?.radio || "").toLowerCase(),
  );
  const request = live?.request;
  const [label, tone] = PHASES[phase] || PHASES.off;
  const radarOptions = Array.from(
    new Set([...RADAR, draft?.live_s].filter(Boolean)),
  )
    .sort((a, b) => a - b)
    .map((v) => [v, `${v} s`]);
  const blocked = !power?.ok || !!waiting;
  const photosPerDay = estimate?.photos ? estimate.photos * 24 : null;
  const quotaLeft =
    storage?.image_quota_bytes > 0
      ? Math.max(0, storage.image_quota_bytes - (storage.image_bytes || 0))
      : null;
  const quotaDays =
    photosPerDay && quotaLeft !== null ? quotaLeft / photosPerDay : null;

  return (
    <section className="field-panel field-live" aria-labelledby="field-live-title">
      <div className="field-panel-head">
        <div>
          <h2 id="field-live-title">Live mode</h2>
          <p>
            Fast radar sampling on external power · windows of up to 4 hours ·
            the node enforces the same limits
          </p>
        </div>
        <span className={`field-state ${tone}`} role="status">
          {phase === "running" && (
            <span className="field-live-dot" aria-hidden="true" />
          )}
          {phase === "running" && live?.interval_s
            ? `Live · every ${live.interval_s} s`
            : label}
        </span>
      </div>
      <div className="field-live-grid">
        <div className="field-live-trace">
          {error && (
            <div className="field-alert" role="alert">
              {error}
            </div>
          )}
          <div className="field-live-reading">
            <div>
              <span className="field-live-label">Radar's own reading</span>
              <strong>{stats ? `${stats.latest.d.toFixed(1)} mm` : "—"}</strong>
              <small>
                {stats
                  ? `Live sample · ${sgt(stats.latest.t, true)} SGT`
                  : open
                    ? "Waiting for the first live sample."
                    : "Start live mode to sample every few seconds."}
              </small>
            </div>
            {until && (
              <div className="field-live-window">
                <span>
                  Window ends <b>{sgt(until)} SGT</b>
                </span>
                <span>{duration(remaining)} left</span>
                {progress !== null && (
                  <div className="field-live-bar" aria-hidden="true">
                    <span style={{ width: `${progress * 100}%` }} />
                  </div>
                )}
              </div>
            )}
          </div>
          {stats && (
            <div className="field-live-stats">
              <div className="field-live-stat">
                <span>Change since {sgt(stats.first.t)}</span>
                <b>{signed(stats.change)} mm</b>
              </div>
              <div className="field-live-stat">
                <span>Trend · last 5 min</span>
                <b>
                  {stats.slope === null ? "—" : `${signed(stats.slope)} mm/min`}
                </b>
              </div>
              <div className="field-live-stat">
                <span>Noise (σ)</span>
                <b>
                  {stats.noise === null ? "—" : `${stats.noise.toFixed(1)} mm`}
                </b>
              </div>
              <div className="field-live-stat">
                <span>Live samples</span>
                <b>
                  {stats.count.toLocaleString()} ·{" "}
                  {Math.round(stats.validShare * 100)}% valid
                </b>
                {stats.cadence && (
                  <small>arriving every {stats.cadence.toFixed(1)} s</small>
                )}
              </div>
            </div>
          )}
          {chartData.length ? (
            <LiveChart data={chartData} baseline={stats ? stats.first.d : null} />
          ) : (
            <p className="field-caption">No samples in the last hour.</p>
          )}
          <p className="field-caption">
            Distance from the radar down to the surface: a rising line means the
            water is falling. Gaps are readings the radar could not confirm;
            grey dots are scheduled samples.
          </p>
        </div>
        <div className="field-live-controls">
          {power && (
            <div
              className={`field-live-power${power.ok ? "" : " is-blocked"}`}
              role={power.ok ? undefined : "alert"}
            >
              {power.ok ? <PlugZap size={16} /> : <Unplug size={16} />}
              <div>
                {power.ok ? (
                  <>
                    <b>On external power.</b> Input{" "}
                    {power.solar_v.toFixed(2)} V
                    {typeof power.battery_v === "number" &&
                      power.battery_v >= 1.5 &&
                      ` · battery ${power.battery_v.toFixed(3)} V`}{" "}
                    · reported {sgt(power.reported_at)} SGT.
                    {nearFloor &&
                      ` The battery is within ${Math.round((power.battery_v - floor) * 1000)} mV of the ${floor.toFixed(2)} V live floor, so a small dip ends the window${draft?.keep ? "; keep-live re-arms it when the battery recovers" : ""}.`}
                  </>
                ) : (
                  power.reason
                )}
              </div>
            </div>
          )}
          {draft && (
            <>
              <Choice
                label="Radar every"
                options={radarOptions}
                value={draft.live_s}
                onChange={change("live_s")}
                disabled={busy}
              />
              <Choice
                label="Window"
                options={WINDOWS}
                value={draft.live_min}
                onChange={change("live_min")}
                disabled={busy}
              />
              <Choice
                label="Camera pairs"
                options={CAMERAS}
                value={draft.live_cam_s}
                onChange={change("live_cam_s")}
                disabled={busy}
              />
              {draft.live_cam_s > 0 && (
                <Choice
                  label="Frame size"
                  options={SIZES.map((s) => [s, s])}
                  value={draft.live_cam_fs}
                  onChange={change("live_cam_fs")}
                  disabled={busy}
                />
              )}
              <label className="field-live-keep">
                <input
                  type="checkbox"
                  checked={draft.keep}
                  disabled={busy}
                  onChange={(e) => change("keep")(e.target.checked)}
                />
                <span>
                  Keep live while on external power
                  <small>
                    Renews the window 30 minutes before it ends, and re-arms it
                    when power returns. Never on battery.
                  </small>
                </span>
              </label>
              {estimate && (
                <p className="field-live-estimate">
                  Radar ≈ <b>{megabytes(estimate.radar)}/h</b>
                  {draft.live_cam_s > 0 &&
                    (estimate.photos === null ? (
                      " · photo size not measured yet"
                    ) : (
                      <>
                        {" "}
                        · photos ≈ <b>{megabytes(estimate.photos)}/h</b>
                      </>
                    ))}{" "}
                  · <b>{megabytes(estimate.window)}</b> per{" "}
                  {duration(draft.live_min * 60)} window.{" "}
                  {cellular
                    ? "The node is on cellular, so this counts against its SIM."
                    : "Measured from this node’s recent reports."}
                </p>
              )}
            </>
          )}
          {draft?.live_cam_s > 0 && (
            <div className="field-live-caution" role="note">
              Camera pairs every {duration(draft.live_cam_s)} keep both camera
              modules powered about{" "}
              {Math.min(100, Math.round((CAMERA_PASS_S / draft.live_cam_s) * 100))}
              % of the time, so they run warm; heat can show up as horizontal
              colour lines in the frames.
              {photosPerDay &&
                ` Photos add about ${megabytes(photosPerDay)} a day${
                  quotaDays !== null
                    ? `, filling the shared ${formatBytes(storage.image_quota_bytes)} photo quota in about ${quotaDays < 1 ? "a day" : `${Math.round(quotaDays)} days`}; after that the oldest originals from every node are removed (thumbnails stay).`
                    : "."
                }`}
              {draft.keep && " Keep-live repeats this until someone stops it."}
            </div>
          )}
          {waiting && (
            <div className="field-notice">
              A settings change sent at {sgt(waiting)} SGT has not reached the
              node yet. Live mode can start once the node confirms it.
            </div>
          )}
          {live?.replaced && (
            <div className="field-alert" role="alert">
              A later settings change replaced this live command before the
              node picked it up. Start it again.
            </div>
          )}
          {actionError && (
            <div className="field-alert" role="alert">
              {actionError}
            </div>
          )}
          <div className="field-actions" style={{ marginTop: 0 }}>
            {open ? (
              <>
                <button
                  type="button"
                  className="field-button primary"
                  disabled={busy || !draft || blocked}
                  onClick={start}
                >
                  {busy ? "Sending…" : "Renew with these settings"}
                </button>
                <button
                  type="button"
                  className="field-button danger"
                  disabled={busy}
                  onClick={() => send({ action: "stop" })}
                >
                  Stop live
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="field-button primary"
                  disabled={busy || !draft || blocked || phase === "stopping"}
                  onClick={start}
                >
                  {busy ? "Sending…" : "Start live"}
                </button>
                {live?.keep && (
                  <button
                    type="button"
                    className="field-button danger"
                    disabled={busy}
                    onClick={() => send({ action: "stop" })}
                  >
                    Stop keeping live
                  </button>
                )}
              </>
            )}
          </div>
          <p className="field-live-trail">
            {request
              ? `${request.automatic ? "Renewed automatically" : request.action === "start" ? "Started" : "Stop sent"} ${sgt(request.created_at)} SGT${request.requested_by && !request.automatic ? ` by ${request.requested_by}` : ""} · ${REQUEST_STATUS[request.status] || request.status.replaceAll("_", " ")}.`
              : "No live command has been sent to this node."}
            {live?.why_text && phase !== "running" && ` Last window: ${live.why_text}`}
            {live?.keep && phase !== "running" && power?.ok && !open &&
              " Keep-live re-arms it at the node’s next report."}
          </p>
        </div>
      </div>
    </section>
  );
}
